import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-secure-payment-overlay',
  template: `
    @if (safeUrl() || htmlContent()) {
      <div class="payment-shell" role="dialog" aria-modal="true" [attr.aria-label]="text('securePayment')">
        <button class="backdrop" type="button" [attr.aria-label]="text('closePayment')" (click)="closed.emit()"></button>
        <section class="payment-window">
          <header>
            <div class="security-mark"><span></span><strong>{{text('aymudoSecure')}}</strong></div>
            <p>{{text('cardLead')}}</p>
            <button type="button" [attr.aria-label]="text('close')" (click)="closed.emit()">×</button>
          </header>
          <div class="frame-wrap">
            @if (frameLoading()) {
              <div class="frame-loading"><i></i><strong>{{text('connecting')}}</strong></div>
            }
            @if (safeUrl()) {
              <iframe
                #paymentFrame
                [src]="safeUrl()"
                [class.is-loading]="frameLoading()"
                [title]="text('aymudoSecure')"
                (load)="frameLoaded()">
              </iframe>
            } @else if (htmlContent()) {
              <iframe
                #paymentFrame
                [srcdoc]="htmlContent()"
                [class.is-loading]="frameLoading()"
                [title]="text('aymudoSecure')"
                (load)="frameLoaded()">
              </iframe>
            }
          </div>
          <footer><span>{{text('secureConnection')}}</span></footer>
        </section>
      </div>
    }
  `,
  styles: [`
    :host{display:block;position:relative;z-index:110}.payment-shell{align-items:center;display:grid;inset:0;padding:clamp(10px,3vw,32px);position:fixed;z-index:110}.backdrop{backdrop-filter:blur(14px);background:color-mix(in srgb,#071417 74%,transparent);border:0;inset:0;position:absolute}.payment-window{background:var(--surface);border:1px solid color-mix(in srgb,var(--accent) 30%,var(--border));border-radius:30px;box-shadow:0 32px 100px rgba(0,0,0,.46);display:grid;grid-template-rows:auto minmax(320px,1fr) auto;height:min(850px,calc(100dvh - 32px));margin:auto;max-height:calc(100dvh - 32px);max-width:720px;min-height:420px;overflow:hidden;position:relative;width:min(100%,720px)}header{align-items:center;border-bottom:1px solid var(--border);display:grid;gap:3px;grid-template-columns:1fr auto;padding:16px 20px}.security-mark{align-items:center;display:flex;gap:10px}.security-mark span{background:#34c77b;border:5px solid color-mix(in srgb,#34c77b 18%,transparent);border-radius:50%;box-sizing:content-box;height:8px;width:8px}.security-mark strong{color:var(--ink)}header p{color:var(--muted);font-size:.8rem;grid-column:1;margin:0 0 0 28px}header button{background:var(--paper);border:1px solid var(--border);border-radius:50%;color:var(--ink);font-size:1.5rem;grid-column:2;grid-row:1/3;height:42px;width:42px}.frame-wrap{display:block;min-height:320px;position:relative}.frame-wrap iframe{background:#fff;border:0;display:block;height:100%;min-height:320px;opacity:1;transition:opacity .2s ease;width:100%}.frame-wrap iframe.is-loading{opacity:0}.frame-loading{align-items:center;background:var(--surface);display:flex;gap:12px;inset:0;justify-content:center;padding:24px;position:absolute;z-index:2}.frame-loading i{animation:secure-spin .8s linear infinite;border:3px solid var(--border);border-radius:50%;border-top-color:var(--accent);height:24px;width:24px}.frame-loading strong{color:var(--ink);font-size:.9rem}footer{align-items:center;border-top:1px solid var(--border);color:var(--muted);display:flex;font-size:.76rem;justify-content:space-between;padding:11px 20px}@keyframes secure-spin{to{transform:rotate(360deg)}}@media(max-width:600px){.payment-shell{padding:0}.payment-window{border-radius:0;height:100dvh;max-height:none;max-width:none;min-height:100dvh;width:100%}header{padding:13px 14px}footer{padding:9px 14px}}
  `]
})
export class SecurePaymentOverlayComponent {
  private readonly i18n=inject(I18nService);
  @ViewChild('paymentFrame') private paymentFrame?: ElementRef<HTMLIFrameElement>;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly returned = new EventEmitter<string>();
  protected readonly frameLoading = signal(true);
  protected rawUrl = '';
  protected readonly htmlContent = signal<SafeHtml | null>(null);
  protected readonly safeUrl = computed<SafeResourceUrl | null>(() => this.trustedUrl());
  private readonly trustedUrl = signal<SafeResourceUrl | null>(null);

  constructor(private readonly sanitizer: DomSanitizer) {}

  protected text(key:string):string{const values:Record<string,Record<string,string>>={
    tr:{securePayment:'Güvenli ödeme',closePayment:'Ödeme ekranını kapat',aymudoSecure:'Aymudo güvenli ödeme',cardLead:'Kart bilgilerin güvenli POS altyapısında işlenir.',close:'Kapat',connecting:'Ödeme kuruluşuna güvenli bağlantı kuruluyor...',secureConnection:'256-bit güvenli bağlantı'},
    en:{securePayment:'Secure payment',closePayment:'Close payment screen',aymudoSecure:'Aymudo secure payment',cardLead:'Your card details are processed through secure POS infrastructure.',close:'Close',connecting:'Establishing a secure connection to the payment provider...',secureConnection:'256-bit secure connection'},
    de:{securePayment:'Sichere Zahlung',closePayment:'Zahlungsfenster schließen',aymudoSecure:'Sichere Zahlung mit Aymudo',cardLead:'Deine Kartendaten werden über eine sichere POS-Infrastruktur verarbeitet.',close:'Schließen',connecting:'Sichere Verbindung zum Zahlungsanbieter wird hergestellt...',secureConnection:'Sichere 256-Bit-Verbindung'},
    es:{securePayment:'Pago seguro',closePayment:'Cerrar pantalla de pago',aymudoSecure:'Pago seguro de Aymudo',cardLead:'Los datos de tu tarjeta se procesan mediante una infraestructura POS segura.',close:'Cerrar',connecting:'Estableciendo una conexión segura con el proveedor de pago...',secureConnection:'Conexión segura de 256 bits'},
    fr:{securePayment:'Paiement sécurisé',closePayment:'Fermer l’écran de paiement',aymudoSecure:'Paiement sécurisé Aymudo',cardLead:'Vos données de carte sont traitées par une infrastructure POS sécurisée.',close:'Fermer',connecting:'Connexion sécurisée au prestataire de paiement...',secureConnection:'Connexion sécurisée 256 bits'}
  };const lang=this.i18n.lang();return(values[lang]??values['en'])[key]??values['en'][key]??key}

  @Input() set url(value: string | null) {
    this.rawUrl = String(value ?? '');
    this.frameLoading.set(Boolean(this.rawUrl));
    this.trustedUrl.set(this.allowUrl(this.rawUrl)
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.rawUrl)
      : null);
  }

  @Input() set html(value: string | null) {
    const html=String(value ?? '');
    this.htmlContent.set(html ? this.sanitizer.bypassSecurityTrustHtml(html) : null);
    this.frameLoading.set(Boolean(html));
  }

  protected frameLoaded(): void {
    this.frameLoading.set(false);
    try {
      const frameUrl = new URL(this.paymentFrame?.nativeElement.contentWindow?.location.href ?? '');
      if (frameUrl.origin !== window.location.origin) return;
      this.returned.emit(`${frameUrl.pathname}${frameUrl.search}${frameUrl.hash}`);
    } catch {
      // Cross-origin access is intentionally blocked until the payment returns to the store.
    }
  }

  private allowUrl(value: string): boolean {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && (
        url.hostname === 'www.aymudo.com'
        || url.hostname === 'aymudo.com'
        || url.origin === window.location.origin
      );
    } catch {
      return false;
    }
  }
}
